import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CheckCircle2, Loader2, AlertTriangle } from 'lucide-react';
import { BTN_PRIMARY, INPUT, LABEL } from './styles';
import {
  BUYER_CATEGORIES,
  NEED_OPTIONS,
  TIMELINE_OPTIONS,
  PROPERTY_RANGES,
  APPLIANCE_RANGES,
  CONTACT,
} from '../content/site';
import { PRODUCTS } from '../content/products';

const BACKEND = process.env.REACT_APP_BACKEND_URL || '';
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/;

const errCls = 'mt-2 font-mono text-[10px] uppercase tracking-[0.15em] text-accent2';
// Sentence-case label for the simplified quote form
const S_LABEL = 'block text-sm font-medium text-dim mb-2';

const PARTNERSHIP_OPTIONS = [
  { value: 'factory', label: 'Purchase RangeSafe knobs for factory installation' },
  { value: 'licensing', label: 'License RangeSafe technology' },
  { value: 'both', label: 'Explore both options' },
  { value: 'unsure', label: 'Not sure yet' },
];

const DEFAULT_NEED = {
  quote: 'quote',
  pilot: 'pilot',
  compatibility: 'compatibility',
  oem: 'oem',
  contact: 'other',
};

const EMPTY = {
  name: '',
  organization: '',
  email: '',
  phone: '',
  buyer_category: '',
  need: '',
  properties_count: '',
  appliance_count: '',
  knob_quantity: '',
  product_interest: '',
  appliance_make_model: '',
  gas_electric: '',
  project_location: '',
  timeline: '',
  application: '',
  annual_volume: '',
  details: '',
  company_url: '',
  partnership_interest: '',
};

export default function InquiryForm({ type = 'quote', submitLabel = 'Submit Inquiry', isOem = false, showCompatibilityFields = false, simple = false, manufacturer = false }) {
  const [params] = useSearchParams();
  const mountedAt = useRef(Date.now());
  const [values, setValues] = useState({
    ...EMPTY,
    need: DEFAULT_NEED[type] || '',
    product_interest: params.get('product') || '',
    partnership_interest: params.get('interest') || '',
  });
  const [errors, setErrors] = useState({});
  const [status, setStatus] = useState('idle');
  const [serverMessage, setServerMessage] = useState('');
  const [result, setResult] = useState(null);

  // Keep the partnership interest in sync when a pathway CTA updates the URL on the same page.
  useEffect(() => {
    const interest = params.get('interest');
    if (interest) setValues((v) => ({ ...v, partnership_interest: interest }));
  }, [params]);

  const set = (k) => (e) => {
    setValues((v) => ({ ...v, [k]: e.target.value }));
    setErrors((er) => ({ ...er, [k]: undefined }));
  };

  const productNames = Object.fromEntries(PRODUCTS.map((p) => [p.slug, p.name]));

  const validate = () => {
    const er = {};
    if (!values.name || values.name.trim().length < 2) er.name = 'Enter your name';
    if (!values.organization || values.organization.trim().length < 2) er.organization = 'Enter your organization';
    if (!values.email || !EMAIL_RE.test(values.email.trim())) er.email = 'Enter a valid email address';
    if (manufacturer && !values.partnership_interest) er.partnership_interest = 'Select a partnership interest';
    if (!simple && !manufacturer && !values.need) er.need = 'Select what you need';
    return er;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    const er = validate();
    setErrors(er);
    if (Object.keys(er).length > 0) {
      setServerMessage('Please correct the highlighted fields.');
      return;
    }
    setServerMessage('');
    setStatus('submitting');
    const industryCtx = params.get('industry') || '';
    const interestLabel = (PARTNERSHIP_OPTIONS.find((o) => o.value === values.partnership_interest) || {}).label || '';
    const ctx = [
      `page: ${window.location.pathname}`,
      industryCtx ? `industry: ${industryCtx}` : '',
      values.product_interest ? `product: ${values.product_interest}` : '',
      interestLabel ? `partnership: ${interestLabel}` : '',
    ]
      .filter(Boolean)
      .join(' · ');
    try {
      const resp = await fetch(`${BACKEND}/api/inquiries`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          ...values,
          type,
          elapsed_ms: Date.now() - mountedAt.current,
          context: ctx,
        }),
      });
      const data = await resp.json().catch(() => ({}));
      if (resp.status === 409) {
        setStatus('success');
        setResult({ duplicate: true });
        return;
      }
      if (resp.status === 429) {
        setStatus('error');
        setServerMessage(data.detail || 'Too many requests. Please try again later.');
        return;
      }
      if (!resp.ok) {
        setStatus('error');
        setServerMessage(data.detail || 'Your inquiry could not be submitted. Please try again or email us directly.');
        return;
      }
      setStatus('success');
      setResult(data);
    } catch (err) {
      setStatus('error');
      setServerMessage('A network error occurred. Please try again or email us directly.');
    }
  };

  if (status === 'success') {
    return (
      <div className="border border-line bg-panel p-8 sm:p-10" data-testid="inquiry-success">
        <CheckCircle2 className="h-8 w-8 text-accentText" aria-hidden="true" />
        <h3 className="mt-5 font-display text-3xl font-bold uppercase tracking-tight text-white">
          {result?.duplicate ? 'Already received' : 'Inquiry received'}
        </h3>
        <p className="mt-4 max-w-md text-sm leading-relaxed text-dim">
          {result?.duplicate
            ? 'This inquiry was already submitted — our Commercial Solutions Team has it. No duplicate was created.'
            : 'Your inquiry has been recorded and routed to the RangeSafe Commercial Solutions Team. For anything urgent, contact us directly.'}
        </p>
        <div className="mt-6 space-y-1 border-t border-line2 pt-6 font-mono text-xs text-dim">
          <p>
            <span className="text-mute">Email </span>
            <a href={`mailto:${CONTACT.commercialEmail}`} className="text-white hover:text-accent2">
              {CONTACT.commercialEmail}
            </a>
          </p>
          <p>
            <span className="text-mute">Phone </span>
            <a href={CONTACT.phoneHref} className="text-white hover:text-accent2">
              {CONTACT.phone}
            </a>
          </p>
          {result?.inquiry_id && (
            <p className="pt-2">
              <span className="text-mute">Reference </span>
              {result.inquiry_id}
            </p>
          )}
        </div>
      </div>
    );
  }

  const err = (k) => errors[k] && <p className={errCls} role="alert">{errors[k]}</p>;
  const L = simple || manufacturer ? S_LABEL : LABEL;

  return (
    <form onSubmit={onSubmit} noValidate data-testid="inquiry-form" className="relative">
      {/* honeypot — hidden from humans */}
      <div className="absolute -left-[9999px] top-auto" aria-hidden="true">
        <label>
          Company URL
          <input
            type="text"
            name="company_url"
            tabIndex={-1}
            autoComplete="off"
            value={values.company_url}
            onChange={set('company_url')}
          />
        </label>
      </div>

      {status === 'error' && serverMessage && (
        <div
          className="mb-8 flex items-start gap-3 border border-accent/50 bg-accent/10 p-4 text-sm text-white"
          role="alert"
          data-testid="inquiry-error"
        >
          <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-accent2" aria-hidden="true" />
          <p>{serverMessage}</p>
        </div>
      )}

      <div className={simple || manufacturer ? 'grid gap-5' : 'grid gap-5 sm:grid-cols-2'}>
        <div>
          <label htmlFor="f-name" className={L}>{simple || manufacturer ? 'Name' : 'Name *'}</label>
          <input id="f-name" type="text" autoComplete="name" className={INPUT} value={values.name} onChange={set('name')} aria-invalid={!!errors.name} aria-describedby={errors.name ? 'f-name-err' : undefined} data-testid="inquiry-name" />
          {errors.name && <p id="f-name-err" className={errCls} role="alert">{errors.name}</p>}
        </div>
        <div>
          <label htmlFor="f-org" className={L}>{simple || manufacturer ? 'Organization' : 'Organization *'}</label>
          <input id="f-org" type="text" autoComplete="organization" className={INPUT} value={values.organization} onChange={set('organization')} aria-invalid={!!errors.organization} aria-describedby={errors.organization ? 'f-org-err' : undefined} data-testid="inquiry-organization" />
          {errors.organization && <p id="f-org-err" className={errCls} role="alert">{errors.organization}</p>}
        </div>
        <div>
          <label htmlFor="f-email" className={L}>{manufacturer ? 'Work email' : simple ? 'Email' : 'Email *'}</label>
          <input id="f-email" type="email" autoComplete="email" className={INPUT} value={values.email} onChange={set('email')} aria-invalid={!!errors.email} aria-describedby={errors.email ? 'f-email-err' : undefined} data-testid="inquiry-email" />
          {errors.email && <p id="f-email-err" className={errCls} role="alert">{errors.email}</p>}
        </div>
        {!manufacturer && (
          <div>
            <label htmlFor="f-phone" className={L}>{simple ? 'Phone' : 'Phone (optional)'}</label>
            <input id="f-phone" type="tel" autoComplete="tel" className={INPUT} value={values.phone} onChange={set('phone')} data-testid="inquiry-phone" />
          </div>
        )}

        {manufacturer && (
          <div>
            <label htmlFor="f-partnership-m" className={L}>Partnership interest</label>
            <select id="f-partnership-m" className={INPUT} value={values.partnership_interest} onChange={set('partnership_interest')} aria-invalid={!!errors.partnership_interest} aria-describedby={errors.partnership_interest ? 'f-partnership-m-err' : undefined} data-testid="inquiry-partnership-interest">
              <option value="">Select…</option>
              {PARTNERSHIP_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
            {errors.partnership_interest && <p id="f-partnership-m-err" className={errCls} role="alert">{errors.partnership_interest}</p>}
          </div>
        )}

        {!simple && !manufacturer && (
          <>
            <div>
              <label htmlFor="f-buyer" className={LABEL}>Buyer category *</label>
              <select id="f-buyer" className={INPUT} value={values.buyer_category} onChange={set('buyer_category')} data-testid="inquiry-buyer-category">
                <option value="">Select…</option>
                {BUYER_CATEGORIES.map((c) => (
                  <option key={c} value={c}>{c}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="f-need" className={LABEL}>What you need *</label>
              <select id="f-need" className={INPUT} value={values.need} onChange={set('need')} aria-invalid={!!errors.need} aria-describedby={errors.need ? 'f-need-err' : undefined} data-testid="inquiry-need">
                <option value="">Select…</option>
                {NEED_OPTIONS.map((n) => (
                  <option key={n.value} value={n.value}>{n.label}</option>
                ))}
              </select>
              {errors.need && <p id="f-need-err" className={errCls} role="alert">{errors.need}</p>}
            </div>

            {values.need === 'oem' || isOem ? (
              <>
                <div className="sm:col-span-2">
                  <label htmlFor="f-partnership" className={LABEL}>Partnership interest</label>
                  <select id="f-partnership" className={INPUT} value={values.partnership_interest} onChange={set('partnership_interest')} data-testid="inquiry-partnership-interest">
                    <option value="">Select…</option>
                    {PARTNERSHIP_OPTIONS.map((o) => (
                      <option key={o.value} value={o.value}>{o.label}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-application" className={LABEL}>Application (optional)</label>
                  <input id="f-application" type="text" className={INPUT} value={values.application} onChange={set('application')} placeholder="e.g. range platform, region" data-testid="inquiry-oem-application" />
                </div>
                <div>
                  <label htmlFor="f-volume" className={LABEL}>Estimated annual volume (optional)</label>
                  <input id="f-volume" type="text" className={INPUT} value={values.annual_volume} onChange={set('annual_volume')} placeholder="No drawings required" data-testid="inquiry-oem-volume" />
                </div>
              </>
            ) : (
              <>
                <div>
                  <label htmlFor="f-props" className={LABEL}>Number of properties</label>
                  <select id="f-props" className={INPUT} value={values.properties_count} onChange={set('properties_count')} data-testid="inquiry-properties">
                    <option value="">Select…</option>
                    {PROPERTY_RANGES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-appl" className={LABEL}>Approximate appliance count</label>
                  <select id="f-appl" className={INPUT} value={values.appliance_count} onChange={set('appliance_count')} data-testid="inquiry-appliance-count">
                    <option value="">Select…</option>
                    {APPLIANCE_RANGES.map((r) => (
                      <option key={r} value={r}>{r}</option>
                    ))}
                  </select>
                </div>
                <div>
                  <label htmlFor="f-knobs" className={LABEL}>Estimated knob quantity, if known</label>
                  <input id="f-knobs" type="text" inputMode="numeric" className={INPUT} value={values.knob_quantity} onChange={set('knob_quantity')} placeholder="Knobs, not appliances" data-testid="inquiry-knob-quantity" />
                </div>
                <div>
                  <label htmlFor="f-product" className={LABEL}>Product interest</label>
                  <select id="f-product" className={INPUT} value={values.product_interest} onChange={set('product_interest')} data-testid="inquiry-product">
                    <option value="">No specific product yet</option>
                    {PRODUCTS.map((p) => (
                      <option key={p.slug} value={p.slug}>{p.name}</option>
                    ))}
                  </select>
                </div>
              </>
            )}

            {(showCompatibilityFields || type === 'compatibility') && (
              <>
                <div>
                  <label htmlFor="f-make" className={LABEL}>Appliance manufacturer / model</label>
                  <input id="f-make" type="text" className={INPUT} value={values.appliance_make_model} onChange={set('appliance_make_model')} placeholder="Model number if known or 'I don't know'" data-testid="inquiry-appliance-model" />
                </div>
                <div>
                  <label htmlFor="f-gas" className={LABEL}>Gas or electric</label>
                  <select id="f-gas" className={INPUT} value={values.gas_electric} onChange={set('gas_electric')} data-testid="inquiry-gas-electric">
                    <option value="">Select…</option>
                    <option value="Gas">Gas</option>
                    <option value="Electric">Electric</option>
                    <option value="I don't know">I don&apos;t know</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label htmlFor="f-location" className={LABEL}>Project location (optional)</label>
              <input id="f-location" type="text" className={INPUT} value={values.project_location} onChange={set('project_location')} placeholder="City, state" data-testid="inquiry-location" />
            </div>
            <div>
              <label htmlFor="f-timeline" className={LABEL}>Desired timeline</label>
              <select id="f-timeline" className={INPUT} value={values.timeline} onChange={set('timeline')} data-testid="inquiry-timeline">
                <option value="">Select…</option>
                {TIMELINE_OPTIONS.map((t) => (
                  <option key={t} value={t}>{t}</option>
                ))}
              </select>
            </div>
          </>
        )}

        <div className={simple || manufacturer ? '' : 'sm:col-span-2'}>
          <label htmlFor="f-details" className={L}>{simple || manufacturer ? 'What prompted you to look for a safer stove knob?' : 'Additional details (optional)'}</label>
          <textarea
            id="f-details"
            rows={simple || manufacturer ? 3 : 4}
            className={simple || manufacturer ? `${INPUT} py-3` : `${INPUT} h-28 py-3`}
            value={values.details}
            onChange={set('details')}
            placeholder={manufacturer ? 'Tell us briefly about your appliance platform or project.' : simple ? 'Tell us a little about your project' : 'Appliance makes, building counts, timelines — anything useful'}
            data-testid="inquiry-details"
          />
        </div>
      </div>

      <div className="mt-8 flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center sm:gap-x-4">
        <button type="submit" className={`${BTN_PRIMARY} shrink-0`} disabled={status === 'submitting'} data-testid="inquiry-submit">
          {status === 'submitting' ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" /> Submitting…
            </>
          ) : (
            submitLabel
          )}
        </button>
        {!simple && !manufacturer && (
          <p className="font-mono text-[10px] uppercase tracking-[0.15em] text-mute">* Required fields.</p>
        )}
      </div>
    </form>
  );
}
